// Login.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ShieldCheckIcon, 
  EyeIcon, 
  EyeSlashIcon,
  AcademicCapIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/Common/LoadingSpinner';

const Login = () => {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // 컴포넌트 마운트 시 스크롤 상단으로 이동
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // 입력 시 에러 클리어
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = '이메일을 입력해주세요! 📧';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '올바른 이메일 형식이 아니에요. 예: example@email.com';
    }
    
    if (!formData.password) {
      newErrors.password = '비밀번호를 입력해주세요! 🔐';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      console.log(' 로그인 시도 시작:', { email: formData.email, timestamp: new Date().toISOString() });
      
      const result = await login(formData.email, formData.password);
      console.log(' 로그인 결과:', result);
      
      if (result.success) {
        // 로그인 성공 시 사용자 역할에 따라 적절한 대시보드로 이동
        const userRole = result.user?.role;
        console.log(' 로그인 성공, 사용자 역할:', userRole);
        console.log(' 네비게이션 시작...');
        
        if (userRole === 'ADMIN' || userRole === 'admin') {
          console.log(' 관리자 대시보드로 이동');
          navigate('/admin/dashboard', { replace: true });
        } else if (userRole === 'TEACHER' || userRole === 'teacher') {
          console.log(' 교사 대시보드로 이동');
          navigate('/teacher/dashboard', { replace: true });
        } else {
          console.log(' 학생 대시보드로 이동');
          navigate('/dashboard', { replace: true });
        }
        
        console.log(' 네비게이션 완료');
      } else {
        // 백엔드에서 제공하는 상세한 에러 메시지 처리
        let errorMessage = result.error || '로그인에 실패했습니다.';
        
        // 에러 코드별 사용자 친화적 메시지 제공
        if (result.details) {
          switch (result.details) {
            case 'MISSING_CREDENTIALS':
              errorMessage = '이메일과 비밀번호를 모두 입력해주세요.';
              break;
            case 'INVALID_CREDENTIALS':
              errorMessage = '앗! 이메일이나 비밀번호가 맞지 않아요. 😅 다시 한번 확인해보세요!';
              break;
            case 'ACCOUNT_INACTIVE':
              errorMessage = '계정이 비활성화되어 있습니다. 관리자에게 문의하세요.';
              break;
            case 'DATABASE_ERROR':
              errorMessage = '서버 연결에 문제가 있습니다. 잠시 후 다시 시도해주세요.';
              break;
            case 'PASSWORD_VERIFICATION_ERROR':
              errorMessage = '비밀번호 검증 중 오류가 발생했습니다. 다시 시도해주세요.';
              break;
            case 'TOKEN_GENERATION_ERROR':
              errorMessage = '로그인 처리 중 오류가 발생했습니다. 다시 시도해주세요.';
              break;
            default:
              errorMessage = result.error || '로그인에 실패했습니다.';
          }
        }
        
        setErrors({ general: errorMessage });
        
        // 개발 환경에서 디버깅을 위한 상세 로그
        if (process.env.NODE_ENV === 'development') {
          console.log('로그인 실패 상세 정보:', {
            error: result.error,
            details: result.details,
            timestamp: new Date().toISOString()
          });
        }
      }
    } catch (error) {
      console.error('로그인 중 오류 발생:', error);
      
      // 네트워크 오류 등 예외 상황 처리
      let errorMessage = '로그인 중 오류가 발생했습니다. 다시 시도해주세요.';
      
      if (error.message && error.message.includes('fetch')) {
        errorMessage = '서버에 연결할 수 없습니다. 인터넷 연결을 확인해주세요.';
      } else if (error.message && error.message.includes('timeout')) {
        errorMessage = '서버 응답이 지연되고 있습니다. 잠시 후 다시 시도해주세요.';
      }
      
      setErrors({ general: errorMessage });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <LoadingSpinner size="lg" message="로그인 중..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full space-y-8"
      >
        {/* 헤더 */}
        <div className="text-center">
          <motion.div
            whileHover={{ rotate: 10 }}
            className="mx-auto h-16 w-16 bg-primary-500 rounded-full flex items-center justify-center mb-4"
          >
            <ShieldCheckIcon className="h-10 w-10 text-white" />
          </motion.div>
          <h2 className="text-3xl font-bold gradient-text">
            SafeAI Kids
          </h2>
          <p className="mt-2 text-gray-600">
            완도교육지원청/Upstage 지원 플랫폼
          </p>
        </div>

        {/* 로그인 폼 */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl p-8"
        >
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* 전체 에러 메시지 */}
            {errors.general && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-red-50 border border-red-200 rounded-lg p-4"
              >
                <p className="text-sm text-red-600">{errors.general}</p>
              </motion.div>
            )}

            {/* 이메일 입력 */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                이메일
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className={`input-field ${errors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="이메일을 입력하세요"
              />
              {errors.email && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-1 text-sm text-red-600"
                >
                  {errors.email}
                </motion.p>
              )}
            </div>

            {/* 비밀번호 입력 */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                비밀번호
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  className={`input-field pr-12 ${errors.password ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="비밀번호를 입력하세요"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                  title={showPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-1 text-sm text-red-600"
                >
                  {errors.password}
                </motion.p>
              )}
              {!errors.password && (
                <p className="mt-1 text-xs text-gray-500">
                  💡 팁: 오른쪽 눈 모양 버튼을 누르면 비밀번호를 볼 수 있어요!
                </p>
              )}
            </div>

            {/* 로그인 버튼 */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-4 text-lg"
            >
              {loading ? '로그인 중...' : '로그인'}
            </motion.button>

            {/* 회원가입 링크 */}
            <div className="text-center">
              <p className="text-sm text-gray-600">
                계정이 없으신가요?{' '}
                <Link
                  to="/register"
                  className="font-medium text-primary-600 hover:text-primary-500 transition-colors"
                >
                  회원가입
                </Link>
              </p>
            </div>
          </form>
        </motion.div>

        {/* 역할 안내 */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-6 shadow-lg"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
            플랫폼 이용 안내
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
              <AcademicCapIcon className="h-8 w-8 text-blue-500" />
              <div>
                <p className="font-medium text-blue-900">학생</p>
                <p className="text-sm text-blue-700">안전한 AI 학습</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
              <UserGroupIcon className="h-8 w-8 text-green-500" />
              <div>
                <p className="font-medium text-green-900">교사</p>
                <p className="text-sm text-green-700">모니터링 & 관리</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* 안전 보장 메시지 */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="flex items-center justify-center space-x-2 text-safe-600">
            <ShieldCheckIcon className="h-5 w-5" />
            <span className="text-sm font-medium">
              모든 활동은 안전하게 보호됩니다
            </span>
          </div>
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-600">
              완도교육지원청과 Upstage가 지원하는 안전한 AI 교육 플랫폼입니다.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Login;
